<template>
    <p v-if="label" class="site-label" :class="`is-${label.tone}`">
        <SvgsSiteClosed v-if="label.tone === 'closed'" class="site-label-icon" />
        <span>{{ label.text }}</span>
    </p>
</template>

<script setup>
    import { useSiteLabel } from '@/composables/useSiteLabel';

    const props = defineProps({
        site: {
            type: Object,
            required: true,
        },
    });

    // Renders nothing when the site has no label, so callers can drop it in
    // without guarding.
    const label = computed(() => useSiteLabel(props.site));
</script>

<style lang="scss" scoped>
    .site-label {
        display: inline-flex;
        align-items: center;
        gap: 6px;

        // Type deliberately identical to .gray-tag (typography.scss), which this
        // sits beside on the site cards — only the colours differ. Mirrored
        // rather than shared because .gray-tag is used across cards, neighborhood
        // pages and detail pages, and this is the only pairing that has to match.
        // Keep both in step if that one changes.
        font-family: $body;
        font-size: 11px;
        font-weight: $heavy;
        letter-spacing: 0.72px;
        line-height: 12px;
        padding: 6px 10px 5px;

        @media (min-width: $tablet) {
            font-size: 12px;
            line-height: 12px;
            padding: 9px 10px 7px;
        }

        text-transform: uppercase;
        color: $white;

        &.is-closed {
            background: $red;
        }

        // Not $turquoise: white on #099f9f is 3.24:1, under the 4.5:1 small
        // text needs. #078282 is the same hue darkened just enough (4.64:1),
        // and only here, where turquoise sits behind small text.
        &.is-day {
            background: #078282;
        }
    }

    // Capped at the line height so the closed variant is exactly as tall as the
    // day variant — a taller icon would grow the flex line and put the two
    // labels back out of step with the neighborhood tag.
    .site-label-icon {
        flex: 0 0 auto;
        width: 11px;
        height: 11px;

        // `align-items: center` centres the icon on the LINE BOX, but uppercase
        // text only inks its cap height sitting on the baseline, so the text's
        // optical centre is higher than the box's and the icon read as low.
        // Measured on the rendered tag: cap height 6.53px against a 10px line box
        // at mobile, 8.71px against 12px at tablet+, leaving the icon 0.69px and
        // 1.25px low respectively. The two differ because the line-height ratio
        // does (10/9 vs 12/12), so one em value would not serve both.
        // Mobile type has since gone from 9/10 to 11/12 (cap height ~7.98px in
        // a 12px box); -1px is that scaled estimate, not a fresh measurement.
        //
        // transform, not margin: a margin would change the flex line height and
        // undo the equal-height guarantee the sizing above exists to provide.
        transform: translateY(-1px);

        @media (min-width: $tablet) {
            width: 12px;
            height: 12px;
            transform: translateY(-1.25px);
        }

        // buttons.scss carries a global `.list { circle, path, line { stroke:
        // $gray-common } }` for the list/map toggle button. `.list` is ALSO the
        // site card's own modifier (Cards/site.vue `&.list`), so on the sites
        // LANDING page that rule captured this icon and painted it #9d9fa2 —
        // while the detail page, which is not inside a `.list`, stayed white.
        // That is why this read as fixed in one place and broken in the other.
        //
        // `stroke="currentColor"` in the SVG is a presentation attribute, which
        // carries no specificity and loses to any selector at all, so the colour
        // has to be restated here where the extra class outranks `.list`.
        :deep([stroke]) {
            stroke: currentColor;
        }

        // The exclamation's dot is fill-only. The same global rule gave it a
        // stroke it was never drawn with, thickening it; take that back off.
        :deep(circle:not([stroke])) {
            stroke: none;
        }
    }
</style>
